import {
  CheckCircleFilled,
  ClearOutlined,
  CloseCircleFilled,
  CloseOutlined,
  DeleteOutlined,
  ShrinkOutlined,
  StopOutlined,
  SyncOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Badge,
  Button,
  Card,
  Dropdown,
  message,
  Progress,
  Space,
  Tooltip,
  Typography,
} from "antd";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  isDataClearActivelyMarked,
  isHiddenFromTaskManager,
} from "../../../app/organization/dataClearConstants";
import useTaskStream from "../../../hooks/useTaskStream";

const { Text } = Typography;

/**
 * TaskItem - Renders an individual progress row for a background task.
 * It uses the useTaskStream hook to subscribe to real-time updates for the specific taskId.
 *
 * @param {object} props - Component props.
 * @param {string} props.taskId - Unique identifier for the task.
 * @param {function} props.onRemove - Callback to remove/dismiss the task.
 */
const TaskItem = ({ taskId, onRemove }) => {
  // Subscribe to real-time updates via EventSource (SSE)
  const streamData = useTaskStream(taskId);
  const { taskState, isConnected, error } = streamData;

  const status = taskState?.status || "PROCESSING";
  const percent = taskState?.progress || 0;
  const statusLine = taskState?.step || "Connecting...";
  const title = taskState?.data?.title || taskState?.title || "Background Task";
  const isFailed = status === "FAILED" || !!error;
  const isHidden = taskState?.is_visible === false;

  // Hidden backend jobs, or company data clear (uses DataClearLock instead).
  if (isHidden || isHiddenFromTaskManager(taskState)) return null;

  /**
   * Returns the appropriate icon based on the current task status.
   */
  const getStatusIcon = () => {
    if (isFailed) return <CloseCircleFilled style={{ color: "#ff4d4f" }} />;
    if (status === "COMPLETED") return <CheckCircleFilled style={{ color: "#52c41a" }} />;
    return <SyncOutlined spin style={{ color: "#1890ff" }} />;
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 6,
        }}
      >
        <Space align="start" size={10} style={{ flex: 1 }}>
          <div style={{ marginTop: 2 }}>{getStatusIcon()}</div>
          <div style={{ flex: 1 }}>
            <Text strong style={{ fontSize: 13, display: "block", lineHeight: "1.4" }}>
              {title}
            </Text>
            <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
              {statusLine}
            </Text>
          </div>
        </Space>

        <div style={{ textAlign: "right", minWidth: 50, marginLeft: 8 }}>
          <Text strong style={{ fontSize: 13, color: isFailed ? "#f5222d" : "inherit" }}>
            {percent}%
          </Text>
          {/* Show remove button only when task is in a terminal state (Completed or Failed) */}
          {(status === "COMPLETED" || isFailed) && (
            <Tooltip title="Remove task from list" styles={{ root: { zIndex: 10001 } }}>
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined style={{ fontSize: 10, color: "#999" }} />}
                onClick={() => onRemove(taskId)}
                style={{ marginLeft: 6, height: 20, width: 20, padding: 0 }}
              />
            </Tooltip>
          )}
        </div>
      </div>

      <div style={{ paddingLeft: 24 }}>
        {error ? (
          <Alert
            message={error}
            type="error"
            size="small"
            showIcon
            style={{ marginTop: 4, fontSize: 11, borderRadius: 6 }}
          />
        ) : (
          <Progress
            percent={percent}
            status={isFailed ? "exception" : status === "COMPLETED" ? "success" : "active"}
            strokeColor={isFailed ? "#ff4d4f" : undefined}
            showInfo={false}
            size="small"
            strokeWidth={6}
          />
        )}

        {/* Real-time Connection Status Indicator */}
        <div
          style={{ display: "flex", justifyContent: "space-between", fontSize: 10, marginTop: 4 }}
        >
          <Space size={4}>
            <span
              style={{
                display: "inline-block",
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: isConnected ? (isFailed ? "#ff4d4f" : "#52c41a") : "#faad14",
                boxShadow: isConnected && !isFailed ? "0 0 4px #52c41a" : "none",
              }}
            />
            <Text type="secondary" style={{ fontSize: 10 }}>
              {isConnected
                ? isFailed
                  ? "Error encountered"
                  : status === "COMPLETED"
                    ? "Task completed"
                    : "Updating in real-time"
                : "Reconnecting..."}
            </Text>
          </Space>
        </div>
      </div>
    </div>
  );
};

/**
 * TaskProgress - The main container for task visualizations.
 * Displays either a TopBar launcher, floating FAB, or an expanded card via portals.
 *
 * @param {object} props - Component props.
 * @param {string[]} props.taskIds - List of active task identifiers.
 * @param {function} props.onToggle - Toggles visibility between card and launcher.
 * @param {function} [props.onClose] - Closes the panel (used for click-outside on TopBar).
 * @param {boolean} props.visible - Whether the expanded card is shown.
 * @param {function} props.onRemoveTask - Logic to remove a single task.
 * @param {function} props.onClearTasks - Logic to batch clear tasks (all/successful).
 * @param {function} props.onAddTask - Logic to add a new task for monitoring.
 * @param {boolean} [props.taskNotificationsMuted] - Whether Do Not Disturb is on.
 * @param {function} [props.onToggleTaskNotificationsMute] - Toggle Do Not Disturb.
 */
const TaskProgress = ({
  taskIds = [],
  onToggle,
  onClose,
  visible = false,
  hidden = false,
  onRemoveTask,
  onClearTasks,
  onAddTask,
  taskNotificationsMuted = false,
  onToggleTaskNotificationsMute,
}) => {
  const rootRef = useRef(null);
  const activeCount = taskIds.length;
  const isIdle = activeCount === 0;
  const companyId = useSelector(
    state => state.auth.user?.company_id ?? state.auth.user?.company?.id,
  );

  // Same pattern as TopBar PopupModal / NotificationBell: mousedown outside closes.
  useEffect(() => {
    if (!visible || typeof onClose !== "function") {
      return undefined;
    }
    const handleClickOutside = event => {
      // Ignore clicks on Ant Design dropdown popups, tooltips, or menus that portal to document.body
      if (event.target.closest?.(".ant-dropdown, .ant-dropdown-menu, .ant-tooltip")) {
        return;
      }
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [visible, onClose]);

  // // API Hooks for external system discovery and sync triggering
  // const { data: externalSystems } = useGetIntegrationExternalSystemsQuery();
  // const [triggerSync] = useTriggerSyncMutation();

  // Keep mounted while clear runs — hide instead of unmount to avoid fade flash.
  // Use active mark only (not the post-clear hold) so the button returns after idle.
  const suppressed = hidden || isDataClearActivelyMarked(companyId);

  /**
   * Triggers a sync action for external systems.
   *
   * @param {string} type - 'all' or 'single'.
   * @param {string} code - System code if type is 'single'.
   * @param {string} name - Human readable name for notifications.
   */
  // const handleSyncAction = async (type, code, name) => {
  //   try {
  //     const payload = type === "all" ? { type: "all" } : { type: "single", code };
  //     const response = await triggerSync(payload).unwrap();

  //     // If the backend returns a taskId, start monitoring it immediately
  //     if (response.task_id && typeof onAddTask === "function") {
  //       onAddTask(response.task_id);
  //     }
  //     message.success(response.detail || `Triggered ${name} sync`);
  //   } catch (error) {
  //     console.error("Sync Error:", error);
  //     message.error(error?.data?.detail || `Failed to trigger ${name} sync`);
  //   }
  // };

  // Dynamically build the sync menu based on systems connected to the company
  // const activeSystems =
  //   externalSystems?.results?.filter(sys => sys.connect_config?.length > 0) || [];

  // const syncMenu = {
  //   items: [
  //     {
  //       key: "sync_all",
  //       label: (
  //         <span>
  //           🚀 <Text strong>Sync All External Systems</Text>
  //         </span>
  //       ),
  //       onClick: () => handleSyncAction("all", null, "Global"),
  //     },
  //     ...(activeSystems.length > 0 ? [{ type: "divider" }] : []),
  //     ...activeSystems.map(sys => ({
  //       key: sys.code,
  //       label: (
  //         <span>
  //           {sys.name}{" "}
  //           <Text type="secondary" style={{ fontSize: 11 }}>
  //             Sync
  //           </Text>
  //         </span>
  //       ),
  //       onClick: () => handleSyncAction("single", sys.code, sys.name),
  //     })),
  //   ],
  // };

  // Menu for bulk clearing terminal state tasks
  const clearMenu = {
    items: [
      {
        key: "all",
        label: "Clear All Tasks (Completed & Failed)",
        icon: <DeleteOutlined />,
      },
      {
        key: "successful",
        label: "Clear Completed Tasks Only",
        icon: <CheckCircleFilled style={{ color: "#52c41a" }} />,
      },
    ],
    onClick: ({ key }) => {
      if (typeof onClearTasks === "function") {
        const clearedCount = onClearTasks(key);
        if (clearedCount > 0) {
          message.success(
            key === "all" ? "All completed & failed tasks cleared" : "Completed tasks cleared",
          );
        } else {
          message.info("No tasks to clear");
        }
      }
    },
  };

  // Absolute under the icon — same offsets as NotificationBell / GlobalCreate (+).
  const topbarCardStyle = {
    position: "absolute",
    top: "calc(100% + 8px)",
    right: 0,
    width: 380,
    maxWidth: "min(380px, calc(100vw - 32px))",
    zIndex: 30,
    boxShadow: "0 6px 24px rgba(0,0,0,0.15)",
    borderRadius: 12,
    border: "1px solid #f0f0f0",
  };

  /**
   * Launcher UI - TopBar header control.
   */
  const launcher = (
    <button
      type="button"
      className={
        visible ? "taskProgressLauncher taskProgressLauncherActive" : "taskProgressLauncher"
      }
      onClick={onToggle}
      aria-expanded={visible}
      aria-label={
        activeCount
          ? `${activeCount} SyncTask Manager task(s)`
          : "SyncTask Manager"
      }
    >
      <Badge count={activeCount} size="small" offset={[6, -2]} color="#1249bf">
        <span className="taskMenuIcon" aria-hidden>
          <UnorderedListOutlined />
        </span>
      </Badge>
    </button>
  );

  /**
   * Expanded Card UI - Detailed list of running and completed tasks.
   */
  const cardContent = (
    <Card
      style={topbarCardStyle}
      styles={{ body: { padding: "16px 20px", maxHeight: "450px", overflowY: "auto" } }}
      size="small"
      title={
        <Space>
          <Text strong>SyncTask Manager</Text>
        </Space>
      }
      extra={
        <Space size={4}>
          {/* <Dropdown
            menu={syncMenu}
            trigger={["click"]}
            placement="bottomRight"
            getPopupContainer={triggerNode => triggerNode.parentNode}
          >
            <Tooltip
              title="Trigger External Sync"
              getPopupContainer={triggerNode => triggerNode.parentNode}
            >
              <Button type="text" size="small" icon={<PlayCircleOutlined />} />
            </Tooltip>
          </Dropdown> */}
          <Tooltip
            title={
              taskNotificationsMuted
                ? "Turn off Do Not Disturb"
                : "Do Not Disturb"
            }
            getPopupContainer={triggerNode => triggerNode.parentNode}
          >
            <Button
              type="text"
              size="small"
              aria-label={
                taskNotificationsMuted
                  ? "Turn off Do Not Disturb"
                  : "Do Not Disturb"
              }
              aria-pressed={taskNotificationsMuted}
              icon={
                <StopOutlined
                  style={taskNotificationsMuted ? { color: "#1249bf" } : undefined}
                />
              }
              onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                if (typeof onToggleTaskNotificationsMute === "function") {
                  onToggleTaskNotificationsMute();
                }
              }}
            />
          </Tooltip>
          <Dropdown
            menu={clearMenu}
            trigger={["click"]}
            placement="bottomRight"
            getPopupContainer={triggerNode => triggerNode.parentNode}
          >
            <Tooltip
              title="Clear"
              getPopupContainer={triggerNode => triggerNode.parentNode}
            >
              <Button
                type="text"
                size="small"
                aria-label="Clear"
                icon={<ClearOutlined />}
              />
            </Tooltip>
          </Dropdown>
          <Button type="text" size="small" onClick={onToggle} icon={<ShrinkOutlined />}></Button>
        </Space>
      }
    >
      {isIdle ? (
        <div style={{ textAlign: "center", padding: "20px 0", color: "#999" }}>
          <UnorderedListOutlined style={{ fontSize: 28, marginBottom: 12, display: "block" }} />
          <Text type="secondary">No active tasks being tracked.</Text>
        </div>
      ) : (
        <div className="task-list">
          {taskIds.map(id => (
            <TaskItem key={id} taskId={id} onRemove={onRemoveTask} />
          ))}
          <div style={{ textAlign: "center", marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>
              Showing {activeCount} active process(es)
            </Text>
          </div>
        </div>
      )}
    </Card>
  );

  // TopBar: relative shell like NotificationBell so the panel anchors under the icon.
  return (
    <div
      ref={rootRef}
      aria-hidden={suppressed ? "true" : undefined}
      className="taskProgressRoot"
      style={suppressed ? { display: "none" } : undefined}
    >
      {launcher}
      {visible ? cardContent : null}
    </div>
  );
};

export default TaskProgress;
